/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaFileAlt, FaSpinner, FaTimes, FaCheckCircle, FaTimesCircle, FaEdit, FaChevronDown, FaChevronUp, FaDownload, FaEye, FaEyeSlash } from 'react-icons/fa'; // Added FaEye and FaEyeSlash
import Menu from '../../components/Menu';
import Button from '../../components/Button';
import api from '../../services/api';
import { useToast } from '../../hooks/toast';

import {
  Container,
  Content,
  Title,
  ContentContainer,
  TitleTable,
  Line,
  Loading,
  ModalContent,
  DivModal,
  ButtonsGroup
} from './styles';

// Interface for Article model
interface Article {
  id: string;
  title: string;
  authors: string;
  abstract: string;
  pdf_url: string; // The URL to the PDF file
  avatar_url: string | null; // The URL to the avatar image
  status: number; // 0: Pendente, 1: Aprovado, 2: Rejeitado
  visible: boolean; // Added visible property
  created_at: Date;
  updated_at: Date;
  open?: boolean; // Added for expandable rows, similar to Associado
}

const ArticleList: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [articleSelected, setArticleSelected] = useState<Article | undefined>(undefined);
  const [modalConfirm, setModalConfirm] = useState(false);
  const [modalDecline, setModalDecline] = useState(false);

  const { addToast } = useToast();

  const loadArticles = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await api.get<Article[]>('/articles');
      const fetchedArticles = response.data.map(article => ({
        ...article,
        created_at: new Date(article.created_at),
        updated_at: new Date(article.updated_at),
        open: false, // Initialize open state for each article
      }));
      setArticles(fetchedArticles);
      console.log(fetchedArticles)
    } catch (error) {
      console.error("Erro ao carregar artigos:", error);
      addToast({
        type: 'error',
        title: 'Erro ao carregar artigos',
        description: 'Não foi possível carregar a lista de artigos.',
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  // Function to map numeric status to a readable string
  const getStatusText = (status: number) => {
    switch (status) {
      case 0:
        return 'Pendente';
      case 1:
        return 'Aprovado';
      case 2:
        return 'Rejeitado';
      default:
        return 'Desconhecido';
    }
  };

  // Function to get status color (optional, you can define these in your styles or directly here)
  const getStatusColorClass = (status: number) => {
    switch (status) {
      case 0:
        return 'status-pending';
      case 1:
        return 'status-approved';
      case 2:
        return 'status-rejected';
      default:
        return '';
    }
  };

  const handleApproveArticle = useCallback(async () => {
    try {
      if (articleSelected?.id) {
        await api.put(`/articles/${articleSelected.id}/status`, { status: 1 }); // 1 for Approved

        addToast({
          type: 'success',
          title: 'Artigo Aprovado',
          description: `O artigo "${articleSelected.title}" foi aprovado com sucesso!`,
        });

        setModalConfirm(false);
        setArticleSelected(undefined);
        loadArticles(); // Reload article list
      }
    } catch (error) {
      console.error("Erro ao aprovar artigo:", error);
      addToast({
        type: 'error',
        title: 'Erro na Aprovação',
        description: 'Ocorreu um erro ao aprovar o artigo.',
      });
    }
  }, [articleSelected, addToast, loadArticles]);

  const handleDeclineArticle = useCallback(async () => {
    try {
      if (articleSelected?.id) {
        await api.put(`/articles/${articleSelected.id}/status`, { status: 2 }); // 2 for Rejected

        addToast({
          type: 'success',
          title: 'Artigo Rejeitado',
          description: `O artigo "${articleSelected.title}" foi rejeitado com sucesso!`,
        });

        setModalDecline(false);
        setArticleSelected(undefined);
        loadArticles(); // Reload article list
      }
    } catch (error) {
      console.error("Erro ao rejeitar artigo:", error);
      addToast({
        type: 'error',
        title: 'Erro na Recusa',
        description: 'Ocorreu um erro ao rejeitar o artigo.',
      });
    }
  }, [articleSelected, addToast, loadArticles]);

  // Functions to close modals
  const handleCloseConfirmModal = useCallback(() => {
    setModalConfirm(false);
    setArticleSelected(undefined);
  }, []);

  const handleCloseDeclineModal = useCallback(() => {
    setModalDecline(false);
    setArticleSelected(undefined);
  }, []);

  // Function to toggle the open/close state of a row
  const toggleRowOpen = useCallback((index: number) => {
    setArticles(prevArticles => {
      const updatedArticles = [...prevArticles];
      updatedArticles[index] = { ...updatedArticles[index], open: !updatedArticles[index].open };
      return updatedArticles;
    });
  }, []);

  // New function to handle article visibility (enable/disable)
  const handleChangeVisibility = useCallback(async (articleParam: Article, status: number) => {
    try {
      // Assuming your API has an endpoint like /articles/visible/:id that toggles 'visible'
      // The payload depends on your API, here we send the opposite of current visible status
      // await api.put(`/articles/visible/${articleParam.id}`, { visible: !articleParam.visible });
      await api.put(`/articles/${articleParam.id}/status`, { status });

      addToast({
        type: 'success',
        title: 'Visibilidade do Artigo Alterada',
        description: `O artigo "${articleParam.title}" foi ${articleParam.visible ? 'desabilitado' : 'habilitado'} com sucesso!`,
      });

      loadArticles(); // Reload article list to reflect the change
    } catch (error) {
      console.error("Erro ao alterar visibilidade do artigo:", error);
      addToast({
        type: 'error',
        title: 'Erro ao Alterar Visibilidade',
        description: 'Ocorreu um erro ao alterar a visibilidade do artigo.',
      });
    }
  }, [addToast, loadArticles]);


  return (
    <Container>
      <Menu />
      <Content>
        <ContentContainer>
          <Title>
            <h1>
              <FaFileAlt size={40} />
              <span>Gerenciamento de Artigos</span>
            </h1>
            <a href="/artigos/criar">
              Submeter Novo Artigo
            </a>
          </Title>

          {loading ? (
            <Loading>
              <FaSpinner size={60} />
              <h1>Carregando Artigos...</h1>
            </Loading>
          ) : (
            articles.length > 0 ? (
              <>
                <TitleTable>
                  <span>Título</span>
                  <span>Autores</span>
                  <span>Status</span>
                  <span>Ações</span>
                </TitleTable>

                {articles.map((article: Article, index) => (
                  <Line key={article.id}>
                    <div>
                      <span>{article.title}</span>
                      <span>{article.authors}</span>
                      <span className={getStatusColorClass(article.status)}>
                        {getStatusText(article.status)}
                      </span>


                      <div className="action_div">
                        {/* Approve/Decline actions for pending articles */}
                        {article.status === 0 && (
                          <>
                            <div
                              className="actions approve-action"
                              onClick={() => {
                                setModalConfirm(true);
                                setArticleSelected(article);
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  setModalConfirm(true);
                                  setArticleSelected(article);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              title="Aprovar Artigo"
                            >
                              <FaCheckCircle size={20} /> Aprovar
                            </div>
                            <div
                              className="actions decline-action"
                              onClick={() => {
                                setModalDecline(true);
                                setArticleSelected(article);
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  setModalDecline(true);
                                  setArticleSelected(article);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              title="Recusar Artigo"
                            >
                              <FaTimesCircle size={20} /> Recusar
                            </div>
                          </>
                        )}

                        {/* Habilitar/Desabilitar action */}
                        {article.status === 1 && (
                          <div
                            className="actions disable-action" // New class for desabilitar
                            onClick={() => handleChangeVisibility(article, 2)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                handleChangeVisibility(article, 2);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            title="Desabilitar Artigo"
                          >
                            <FaEyeSlash size={20} /> Desabilitar
                          </div>
                        )}

                        {
                          article.status === 2 && (
                            <div
                              className="actions enable-action" // New class for habilitar
                              onClick={() => handleChangeVisibility(article, 1)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  handleChangeVisibility(article, 1);
                                }
                              }}
                              role="button"
                              tabIndex={0}
                              title="Habilitar Artigo"
                            >
                              <FaEye size={20} /> Habilitar
                            </div>
                          )
                        }
                        {/* Edit Action */}
                        <div className="actions">
                          <Link to={`/artigos/alterar/${article.id}`} title="Alterar Artigo">
                            <FaEdit size={20} /> Alterar
                          </Link>
                        </div>

                        {/* Expand/Collapse details */}
                        <div
                          className="actions"
                          onClick={() => toggleRowOpen(index)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              toggleRowOpen(index);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          title={article.open ? "Fechar Detalhes" : "Ver Detalhes"}
                        >
                          {!article.open ? (
                            <>
                              <FaChevronDown size={20} /> Detalhes
                            </>
                          ) : (
                            <>
                              <FaChevronUp size={20} /> Fechar
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Collapsible content area */}
                    {article.open && (
                      <div className="content_div">
                        <div>
                          <h3>Informações Detalhadas</h3>
                          <span>
                            Resumo: {article.abstract || 'N/A'}
                          </span>
                          <span>
                            URL do PDF:
                            {article.pdf_url ? (
                              <a href={article.pdf_url} target="_blank" rel="noopener noreferrer">
                                <FaDownload size={16} style={{ verticalAlign: 'middle', marginRight: '5px', marginLeft: '5px' }} /> Baixar PDF
                              </a>
                            ) : 'N/A'}
                          </span>
                          <span>
                            Última Atualização: {new Date(article.updated_at).toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>
                    )}
                  </Line>
                ))}
              </>
            ) : (
              <h3>Nenhum artigo encontrado</h3>
            )
          )}
        </ContentContainer>
      </Content>

      {/* Modals for Approve/Decline Confirmation */}
      {(modalConfirm || modalDecline) && (
        <ModalContent>
          {modalConfirm && articleSelected && (
            <DivModal>
              <div>
                <FaTimes size={20} onClick={handleCloseConfirmModal} style={{ cursor: 'pointer' }} />
              </div>
              <h2>Você tem certeza que deseja aprovar este artigo?</h2>
              <p>
                Título: <strong>{articleSelected.title}</strong>
              </p>
              <p>
                Autores: {articleSelected.authors}
              </p>
              <br />
              <ButtonsGroup>
                <Button type="button" color="#E84545" onClick={handleCloseConfirmModal}>Cancelar</Button>
                <Button type="button" color="#7BDC4D" onClick={handleApproveArticle}>Confirmar</Button>
              </ButtonsGroup>
            </DivModal>
          )}

          {modalDecline && articleSelected && (
            <DivModal>
              <div>
                <FaTimes size={20} onClick={handleCloseDeclineModal} style={{ cursor: 'pointer' }} />
              </div>
              <h2>Você tem certeza que deseja recusar este artigo?</h2>
              <p>
                Título: <strong>{articleSelected.title}</strong>
              </p>
              <p>
                Autores: {articleSelected.authors}
              </p>
              <br />
              <ButtonsGroup>
                <Button type="button" color="#E84545" onClick={handleCloseDeclineModal}>Cancelar</Button>
                <Button type="button" color="#7BDC4D" onClick={handleDeclineArticle}>Confirmar</Button>
              </ButtonsGroup>
            </DivModal>
          )}
        </ModalContent>
      )}
    </Container>
  );
};

export default ArticleList;
